// src/pages/ia-paca/nice.tsx
//
// Page géo — Nice (Métropole Nice Côte d'Azur, FinTech, gaming, tourisme).
// Stratégie : capter "recrutement IA Nice", "Data Scientist Nice",
// "AI Engineer Côte d'Azur", "MedTech IA Nice".

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import IaPacaHero from '../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  ArrowRight,
  Cpu,
  GraduationCap,
  Building2,
  Heart,
  Gamepad2,
  MapPin,
} from 'lucide-react';

export default function NiceIaPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Recruteurs.io — Recrutement IA Nice',
    description:
      "Cabinet de recrutement IA à Nice. Profils AI Engineer, Data Scientist, MLOps pour les startups, scale-ups, MedTech et HealthTech de la Métropole Nice Côte d'Azur.",
    url: 'https://recruteurs.io/ia-paca/nice',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Nice',
      addressRegion: "Provence-Alpes-Côte d'Azur",
      addressCountry: 'FR',
    },
    areaServed: { '@type': 'City', name: 'Nice' },
  };

  return (
    <Layout
      title="Recrutement IA à Nice — AI Engineer, Data Scientist | recruteurs.io"
      description="Cabinet de recrutement IA à Nice. Métropole Nice Côte d'Azur, IMREDD, Université Côte d'Azur, MedTech, gaming, FinTech. Cabinet local pour AI Engineer, Data Scientist, MLOps."
      keywords="recrutement IA Nice, AI Engineer Nice, Data Scientist Nice, recrutement intelligence artificielle Nice Côte d'Azur, IMREDD, Université Côte d'Azur, MedTech Nice"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Nice', href: '/ia-paca/nice' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Recrutement IA · Nice"
        title="Recruter en IA"
        highlight="à Nice."
        subtitle="Nice n'est pas Sophia. La métropole Nice Côte d'Azur (550 000 hab.) joue une carte différente : MedTech / HealthTech, gaming, FinTech, smart city, tourisme augmenté. Un vivier de profils IA complémentaire à Sophia, et un cadre de vie qui attire des seniors en relocalisation depuis Paris."
        badges={['IMREDD', 'Université Côte d\'Azur', '3IA Côte d\'Azur', 'CHU Nice', 'EDHEC']}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Toutes les villes IA en PACA
          </Link>
        </div>
      </div>

      {/* Marché Nice */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Marché IA & tech — Nice 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Nice : la métropole IA grand public et MedTech de la Côte d'Azur.
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              <strong>Nice ≠ Sophia.</strong> Là où Sophia concentre la R&D et
              les corporates tech, Nice est plus tournée vers les{' '}
              <strong>applications IA grand public, santé, mobilité, tourisme</strong>{' '}
              et les startups deeptech issues de l'Université Côte d'Azur
              (UniCA, 30 000 étudiants).
            </p>
            <p>
              Le moteur académique principal est l'<strong>IMREDD</strong>{' '}
              (Institut Méditerranéen du Risque, de l'Environnement et du
              Développement Durable, 2 500 m² au cœur de l'éco-vallée),
              orienté smart city, gestion de l'eau, énergie, mobilité. Côté
              santé, le <strong>CHU Nice + Centre Antoine Lacassagne</strong>{' '}
              forment un pôle MedTech / IA santé en croissance.
            </p>
            <p>
              Pour les recruteurs IA : Nice attire des seniors en
              relocalisation depuis Paris (qualité de vie, prix immobilier
              encore raisonnable vs Antibes / Cannes), et offre un pool de
              juniors UniCA / EDHEC en forte croissance.
            </p>
          </div>
        </div>
      </section>

      {/* Écosystème Nice */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Écosystème Nice
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            IMREDD, UniCA, CHU — l'IA santé & smart city.
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
            {ECOSYSTEME_NICE.map((e) => (
              <div
                key={e.nom}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-center gap-2">
                  <e.Icon className="h-5 w-5 text-violet-600" />
                  <h3 className="text-base font-bold text-slate-900">{e.nom}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-600">{e.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profils types Nice */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Qui recrute en IA à Nice ?
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            4 typologies d'employeurs IA à Nice.
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            <TypologieCard
              Icon={Heart}
              tag="MedTech / HealthTech"
              title="IA santé, imagerie, diagnostic"
              body="CHU Nice + Centre Antoine Lacassagne (cancer) + startups MedTech. Cas d'usage : imagerie médicale, prédiction de pathologies, optimisation parcours patient. Profils Data Scientists santé, ML imaging."
            />
            <TypologieCard
              Icon={Building2}
              tag="Smart City & énergie"
              title="IMREDD, Métropole, EDF / Engie"
              body="L'éco-vallée de la Plaine du Var + IMREDD pilotent des projets smart city, mobilité, gestion de l'eau, optimisation énergétique. Profils Data Engineering temps réel + ML."
            />
            <TypologieCard
              Icon={Gamepad2}
              tag="Gaming / Tourisme augmenté"
              title="Casinos, gaming studios, AR/VR"
              body="Tradition gaming sur la Côte d'Azur (Cannes, Monaco), studios indépendants à Nice. IA appliquée : génération procédurale, NPC, anti-fraude jeux d'argent, réalité augmentée tourisme."
            />
            <TypologieCard
              Icon={Cpu}
              tag="FinTech / PME tech"
              title="Asset management, SaaS B2B"
              body="Effet Monaco : asset management, family offices, FinTech locales. PME SaaS B2B en croissance. Profils Data Science finance, AI Engineer fraude/risque, MLOps temps réel."
            />
          </div>
        </div>
      </section>

      {/* Salaires Nice */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires IA Nice — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Nice vs Sophia : -2 à -8% selon poste, marché plus liquide en junior.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Les packages IA à Nice sont légèrement inférieurs à Sophia (où
            les corporates tirent les prix), mais la disponibilité est
            meilleure côté juniors et profils confirmés. Effet Monaco sur les
            packages FinTech (premium +10-20%).
          </p>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            FAQ Nice
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les questions du marché niçois.
          </h2>
          <div className="mt-8 space-y-3">
            {FAQ_NICE.map((q) => (
              <details
                key={q.question}
                className="group rounded-2xl border border-slate-200 bg-white p-5"
              >
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                  <span>{q.question}</span>
                  <span className="mt-0.5 text-slate-400 transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <div className="mt-4 text-sm leading-relaxed text-slate-600">
                  {q.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Lien autres villes */}
      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-pink-600" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-900">
                  Vous recrutez aussi sur Sophia ou le reste de la PACA ?
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Pages dédiées :{' '}
                  <Link
                    to="/ia-paca/sophia-antipolis"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Sophia Antipolis
                  </Link>
                  {' · '}
                  <Link
                    to="/ia-paca/marseille"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Marseille
                  </Link>
                  {' · '}
                  <Link
                    to="/ia-paca/aix-en-provence"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Aix-en-Provence
                  </Link>
                  {' · '}
                  <Link
                    to="/ia-paca"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Vue d'ensemble PACA
                  </Link>
                  .
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous recrutez à Nice ou sur la Côte d'Azur ?"
        subtitle="Notre cabinet couvre tout l'arc Nice / Sophia / Antibes / Cannes / Monaco. Brief 30 min, court-list en 15-25 jours selon le poste."
      />
    </Layout>
  );
}

function TypologieCard({
  Icon,
  tag,
  title,
  body,
}: {
  Icon: typeof Building2;
  tag: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
        <Icon className="h-5 w-5" />
      </div>
      <span className="mt-3 inline-block rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-pink-700">
        {tag}
      </span>
      <h3 className="mt-2 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

const ECOSYSTEME_NICE = [
  {
    nom: 'IMREDD',
    Icon: Building2,
    description:
      "Institut Méditerranéen du Risque, de l'Environnement et du Développement Durable. 2 500 m² au cœur de l'éco-vallée. Smart city, gestion de l'eau, énergie, mobilité, jumeau numérique urbain.",
  },
  {
    nom: 'Université Côte d\'Azur (UniCA)',
    Icon: GraduationCap,
    description:
      "30 000 étudiants. Labellisée Université d'Excellence. Masters IA, Data Science, MIAGE. Spin-offs régulières via le 3IA Côte d'Azur (partagé avec Sophia).",
  },
  {
    nom: 'CHU Nice + Centre Antoine Lacassagne',
    Icon: Heart,
    description:
      "Pôle santé majeur. CHU Nice (3 sites, 3 000 lits). Centre Antoine Lacassagne (oncologie). IA santé : imagerie médicale, oncologie computationnelle, prédiction de pathologies.",
  },
  {
    nom: 'EDHEC Business School',
    Icon: GraduationCap,
    description:
      "Campus EDHEC à Nice. Spécialisation finance, asset management. Forte employabilité dans la FinTech locale et l'écosystème Monaco. Source de profils business + data.",
  },
  {
    nom: '3IA Côte d\'Azur',
    Icon: Cpu,
    description:
      "Partagé avec Sophia. Institut interdisciplinaire IA. Forte composante santé à Nice, smart city, mobilité. Source de PhDs et spin-offs deeptech.",
  },
  {
    nom: 'CEEI Nice Côte d\'Azur (CEEI)',
    Icon: Building2,
    description:
      "Centre Européen d'Entreprises et d'Innovation à Nice. Suit les startups innovantes (dont IA, MedTech, GreenTech). Bonne porte d'entrée pour le réseau startup local.",
  },
];

const FAQ_NICE = [
  {
    question: "Quelle est la différence entre recruter en IA à Nice et à Sophia Antipolis ?",
    answer:
      "Sophia = R&D, corporates tech (Amadeus, IBM, Renesas), profils PhD et ML Research. Nice = applications IA santé, smart city, mobilité, gaming, FinTech. Stack souvent plus 'produit' à Nice (full-stack ML), plus 'recherche' à Sophia. Salaires Sophia +5-10% vs Nice sur les profils tech purs, mais Nice prime sur la FinTech (effet Monaco).",
  },
  {
    question: "Combien de profils IA disponibles sur Nice + Sophia + Côte d'Azur ?",
    answer:
      "Notre estimation : ~1 500 à 2 000 profils IA / Data Science sur l'arc Nice + Sophia + Antibes + Cannes. Nice concentre ~30% de ce pool (le reste sur Sophia + arc Antibes-Cannes). En veille passive activable : 25-35% selon poste.",
  },
  {
    question: "Vous recrutez aussi pour Monaco ?",
    answer:
      "Oui. Notre périmètre Côte d'Azur inclut Monaco pour les profils IA dans la FinTech, l'asset management, le gaming et la cybersécurité. Particularités : packages premium (statut monégasque ou frontalier), exigence anglais business, process plus formels. Cabinet présent localement.",
  },
  {
    question: "Comment recrutez-vous des profils MedTech / IA santé ?",
    answer:
      "Les profils MedTech IA combinent souvent : background data science + connaissance métier santé (imagerie, oncologie, génomique). On chasse via CHU Nice, Centre Antoine Lacassagne, INSERM, et les écoles ingé biomédicale. Process plus long (8-12 semaines), candidats rares mais identifiables.",
  },
  {
    question: "Le marché IA Nice est-il porteur en 2026 ?",
    answer:
      "Oui, en croissance soutenue. Drivers : (1) effet IMREDD sur smart city, (2) digitalisation CHU/Lacassagne, (3) startups MedTech qui lèvent (série A/B), (4) effet 3IA + Pôle Alpha Sophia qui irrigue Nice, (5) relocalisation Paris → Côte d'Azur en croissance post-2024. Volume offres en hausse +25-35% sur 12 mois.",
  },
];
